/**
 * Reduction Mission sub-KPI type.
 *
 * A Reduction project tracks a measured value DECREASING over time toward
 * a target threshold. Score reflects how much progress has been made from
 * the starting baseline toward the target.
 *
 * Examples: PFAS in soil concentration, beach trash weight, lake pollutant
 * levels, incident rates, response times.
 *
 * Scoring math:
 *   - If current ≤ target: score = 100 (target met)
 *   - If current ≥ baseline: score = 0 (regression past starting point)
 *   - Otherwise: score = round(100 × (baseline - current) / (baseline - target))
 *
 * Trend logic uses direction-aware delta with goal_direction='decrease'
 * (a falling value is improving).
 *
 * See: DEV-814 KPI Engine Refinement, Phase 2 — first new sub-KPI type.
 */

import type {
  ConfigFieldSchema,
  MissionDataPoint,
  MissionSubKpiType,
  SetupExample,
  SubKpiScore,
  UiHints,
  ValidationError, // DEV-963: surfaced after TS 5.4 upgrade.
} from './types';
import { latestAsOf } from '../timeSeries';
import { KPI_DEFAULTS } from '../defaults';
import { validateCommonCrossFields } from './commonValidations';

/**
 * Configuration for a Reduction sub-KPI instance.
 *
 * Set at template authoring time and inherited by each project instance
 * (with optional per-instance overrides for baseline/target/deadline).
 */
export interface ReductionConfig extends Record<string, unknown> {
  /** Display name for the metric (e.g., "PFAS in Soil"). */
  display_name: string;
  /** Initial measured value at project start. */
  baseline_value: number;
  /** Target threshold to reach (must be < baseline_value). */
  target_value: number;
  /** Unit of measure for display (e.g., "ppm", "lb/week"). */
  unit_of_measure: string;
  /** Optional deadline (YYYY-MM-DD) for reaching the target. */
  deadline_date?: string;
  /** Score >= this is green. Default 80. */
  green_threshold?: number;
  /** Score >= this (and < green_threshold) is yellow. Default 60. */
  yellow_threshold?: number;
  /** Trend epsilon in raw value units. Default 0 (any change registers). */
  trend_epsilon?: number;
}

/* ------------------------------------------------------------------------- */
/*  Scoring                                                                  */
/* ------------------------------------------------------------------------- */

function scoreReduction(
  config: ReductionConfig,
  data: MissionDataPoint[],
  asOfDate: string,
): SubKpiScore {
  const { baseline_value, target_value, unit_of_measure } = config;

  // Convert MissionDataPoint[] to the shape latestAsOf expects.
  const pts = data.map((d) => ({ date: d.date, value: d.value }));
  const cur = latestAsOf(pts, asOfDate);

  if (!cur) {
    return {
      score_0_100: null,
      progress_0_100: null,
      current_value: null,
      target_value,
      baseline_value,
      explain: 'No reduction data available as of this date.',
    };
  }

  const currentValue = cur.value;

  // Target met (current at or below target).
  if (currentValue <= target_value) {
    return {
      score_0_100: 100,
      // DEV-864: At or below target → 100% raw progress.
      // Note: reduction.ts does not currently use applyDeadlineDegradation, so
      // score_0_100 == progress_0_100 here. Kept symmetric with the other types
      // for contract consistency.
      progress_0_100: 100,
      current_value: currentValue,
      target_value,
      baseline_value,
      explain:
        `Target met: ${currentValue} ${unit_of_measure} ` +
        `at or below target ${target_value} ${unit_of_measure}.`,
    };
  }

  // Regression past baseline (current at or above starting point).
  if (currentValue >= baseline_value) {
    return {
      score_0_100: 0,
      // DEV-864: At or above baseline → 0% raw progress (regression).
      progress_0_100: 0,
      current_value: currentValue,
      target_value,
      baseline_value,
      explain:
        `Regression: current ${currentValue} ${unit_of_measure} ` +
        `at or above baseline ${baseline_value} ${unit_of_measure}.`,
    };
  }

  // Partial progress between baseline and target.
  const span = baseline_value - target_value;
  const progress = baseline_value - currentValue;
  const score = Math.round(
    Math.max(0, Math.min(100, (progress / span) * 100)),
  );

  return {
    score_0_100: score,
    // DEV-864: Raw progress = ((baseline - current) / (baseline - target)) * 100.
    // No deadline degradation in reduction.ts, so progress_0_100 == score_0_100.
    progress_0_100: score,
    current_value: currentValue,
    target_value,
    baseline_value,
    explain:
      `Reduced from ${baseline_value} to ${currentValue} ${unit_of_measure} ` +
      `(target ${target_value} ${unit_of_measure}); ${score}% of the way.`,
  };
}

/* ------------------------------------------------------------------------- */
/*  Color derivation                                                         */
/* ------------------------------------------------------------------------- */

function colorFromScore(
  score: SubKpiScore,
  config: ReductionConfig,
): 0 | 1 | 2 | 3 {
  if (score.score_0_100 === null) return 0;
  const greenAt = config.green_threshold ?? KPI_DEFAULTS.mission.bands.green_threshold;
  const yellowAt = config.yellow_threshold ?? KPI_DEFAULTS.mission.bands.yellow_threshold;
  if (score.score_0_100 >= greenAt) return 3;
  if (score.score_0_100 >= yellowAt) return 2;
  return 1;
}

/* ------------------------------------------------------------------------- */
/*  Validation                                                               */
/* ------------------------------------------------------------------------- */

function validateConfig(config: ReductionConfig): ValidationError | null {
  // DEV-816 / Skip: per-field required checks live in the schema
  // (`required: true`) and are enforced by the wizard. Duplicates here
  // caused premature banner errors before user interaction.
  // Cross-field rule: target must be lower than baseline (it's a reduction).
  if (
    Number.isFinite(config.target_value) &&
    Number.isFinite(config.baseline_value) &&
    config.target_value >= config.baseline_value
  ) {
    return {
      field: 'target_value',
      message: 'Target must be lower than baseline (this is a Reduction type).',
    };
  }
  return validateCommonCrossFields(config);
}

/* ------------------------------------------------------------------------- */
/*  Schema, examples, and UI hints                                           */
/* ------------------------------------------------------------------------- */

const config_schema: ConfigFieldSchema[] = [
  {
    key: 'display_name',
    label: 'What are you reducing?',
    type: 'string',
    required: true,
    help_text:
      'A short name for the measurement (e.g., "PFAS in Soil", "Beach Trash").',
  },
  {
    key: 'baseline_value',
    label: 'Starting (baseline) value',
    type: 'number',
    required: true,
    help_text:
      'The measured value at the start of the project. Score is computed against this baseline.',
    inline_with_next: true, // pair with target_value (DEV-816)
  },
  {
    key: 'target_value',
    label: 'Target value',
    type: 'number',
    required: true,
    help_text: 'The value you aim to reduce to. Must be lower than baseline.',
  },
  {
    key: 'unit_of_measure',
    label: 'Unit of measure',
    type: 'string',
    required: true,
    help_text: 'E.g., "ppm", "lb/week", "incidents/month".',
  },
  {
    key: 'tracking_start_date',
    label: 'Tracking start date (optional)',
    type: 'date',
    required: false,
    help_text: 'Scoring begins on this date. Reporting periods before this date are ignored.',
    inline_with_next: true, // pair with deadline_date (DEV-816)
  },
  {
    key: 'deadline_date',
    label: 'Target deadline (optional)',
    type: 'date',
    required: false,
    help_text: 'When the target should be reached. Used for forecasting only.',
  },
  {
    key: 'color_mode',
    label: 'Color the indicator from',
    type: 'enum',
    required: false,
    default_value: 'score',
    enum_options: [
      {
        value: 'score',
        label: 'Reduction (% reduced)',
        help: 'Color is driven by how much of the planned reduction has been achieved (baseline → target). Set the % thresholds below for green/yellow/red.',
      },
      {
        value: 'deadline',
        label: 'Time remaining until deadline',
        help: 'Color is driven by days remaining: green far from deadline, yellow within the warning window, red within the critical window. Useful when the timeline matters more than the reduction %.',
      },
    ],
    help_text: 'Pick how the indicator color is computed.',
  },
  // Labels reframed for consistency (DEV-814 / Skip): see count.ts.
  {
    key: 'green_threshold',
    label: 'Yellow when % of reduction drops below',
    type: 'number',
    required: false,
    default_value: 80,
    suffix: '%',
    help_text: 'Indicator turns yellow once the % of planned reduction achieved drops below this number. Default 80.',
    validation: { min: 0, max: 100 },
    visible_when: (c) => (c.color_mode ?? 'score') === 'score',
    inline_with_next: true, // pair with red threshold (DEV-816)
  },
  {
    key: 'yellow_threshold',
    label: 'Red when % of reduction drops below',
    type: 'number',
    required: false,
    default_value: 60,
    suffix: '%',
    help_text: 'Indicator turns red once the % of planned reduction achieved drops below this number. Must be less than yellow. Default 60.',
    validation: { min: 0, max: 100 },
    visible_when: (c) => (c.color_mode ?? 'score') === 'score',
  },
  // Deadline-mode windows reframed (DEV-816 / Skip) to "drops below" pattern.
  {
    key: 'warning_days',
    label: 'Yellow when days until deadline drops below',
    type: 'number',
    required: false,
    default_value: 30,
    suffix: 'days',
    help_text: 'Indicator turns yellow once fewer than this many days remain until the deadline. Default 30.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'deadline',
    inline_with_next: true, // pair with critical_days (DEV-816)
  },
  {
    key: 'critical_days',
    label: 'Red when days until deadline drops below',
    type: 'number',
    required: false,
    default_value: 7,
    suffix: 'days',
    help_text: 'Indicator turns red once fewer than this many days remain until the deadline. Must be less than yellow. Default 7.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'deadline',
  },
  {
    key: 'trend_epsilon',
    label: 'Trend sensitivity (epsilon)',
    type: 'number',
    required: false,
    default_value: 0,
    help_text:
      'A change smaller than this is treated as "no material change" for trend. ' +
      'Set higher to suppress noise from minor measurement fluctuations.',
  },
];

const setup_examples: SetupExample[] = [
  {
    title: 'PFAS in soil reduction',
    description:
      'Reduce per- and polyfluoroalkyl substance concentration in contaminated soil ' +
      'from baseline 50 ppm to target 10 ppm through remediation.',
    example_config: {
      display_name: 'PFAS in Soil',
      baseline_value: 50,
      target_value: 10,
      unit_of_measure: 'ppm',
      deadline_date: '2027-12-31',
    },
  },
  {
    title: 'Beach trash reduction',
    description:
      'Reduce weekly trash collected from baseline 500 lb/week to target 50 lb/week ' +
      'through community cleanups and source reduction outreach.',
    example_config: {
      display_name: 'Beach Trash',
      baseline_value: 500,
      target_value: 50,
      unit_of_measure: 'lb/week',
    },
  },
  {
    title: 'Lake pollutant reduction',
    description:
      'Reduce pollutant concentration in lake from baseline 25 mg/L to target 5 mg/L ' +
      'through watershed management and source-control interventions.',
    example_config: {
      display_name: 'Lake Pollutants',
      baseline_value: 25,
      target_value: 5,
      unit_of_measure: 'mg/L',
    },
  },
];

const ui_hints: UiHints = {
  default_chart: 'line',
  value_format: 'number',
  show_target_line: true,
};

/* ------------------------------------------------------------------------- */
/*  Export                                                                   */
/* ------------------------------------------------------------------------- */

export const reductionType: MissionSubKpiType<ReductionConfig> = {
  type_key: 'reduction',
  display_name: 'Reduction',
  description:
    'Track a value that should fall from a baseline toward a lower target.',
  examples: [
    'emissions per ton',
    'defect rate',
    'response time',
  ],
  use_when: 'How much lower is it now?',
  supported_color_modes: ['score', 'deadline'],
  goal_direction: 'decrease',
  config_schema,
  validate_config: validateConfig,
  score: scoreReduction,
  color_from_score: colorFromScore,
  ui_hints,
  setup_examples,
};
